"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  CircleX,
  TextAlignEnd,
  BarChart3,
  BookOpen,
  Zap,
} from "lucide-react";
import { ACCOUNT_DASHBOARD_URL, LOGIN_URL } from "@/utils/constants";
import useSession from "@/hooks/useSession";
import AccountMenu, { AccountMenuMobile } from "@/app/components/AccountMenu";

const navLinks = [
  {
    href: "/features",
    label: "Products",
    hasDropdown: true,
    dropdownKey: "products",
  },
  { href: "/pricing/books", label: "Pricing", matchPrefix: "/pricing" },
  { href: "/about", label: "About" },
  { href: "/blog", label: "Blog" },
  { href: "/documentation", label: "Docs" },
  { href: "/contact", label: "Contact" },
];

const featuresDropdown = [
  {
    href: "/features/crm",
    label: "ABSS CRM",
    description: "Leads, pipeline & sales automation",
    icon: BarChart3,
    color: "#171717",
    bg: "#F5F5F5",
  },
  {
    href: "/features/books",
    label: "ABSS Books",
    description: "GST accounting & invoicing",
    icon: BookOpen,
    color: "#171717",
    bg: "#F5F5F5",
  },
];

const navDropdowns = {
  products: {
    items: featuresDropdown,
  },
};

export default function Header() {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const dropdownTimeout = useRef(null);
  const [renderSidebar, setRenderSidebar] = useState(false);
  const [animateSidebar, setAnimateSidebar] = useState(false);
  const { status, user } = useSession();
  const isAuthenticated = status === "authenticated" && user;

  useEffect(() => {
    if (isMenuOpen) {
      setRenderSidebar(true);
      setTimeout(() => setAnimateSidebar(true), 30);
    } else {
      setAnimateSidebar(false);
      setTimeout(() => setRenderSidebar(false), 300);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "unset";
  }, [isMenuOpen]);

  const handleMouseEnter = (key) => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setOpenDropdown(key);
  };

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <header className="header-rails sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E5E5E5] overflow-visible">
      <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
        {/* TOP BAR */}
        <div className="flex items-center justify-between gap-3 h-14 sm:h-16 md:h-[68px] min-w-0">
          {/* Logo */}
          <Link href="/" className="flex items-center shrink-0 min-w-0">
            <img
              src="/ABSS-LOGO.png"
              alt="ABSS"
              className="h-9 w-auto sm:h-10 md:h-12 max-w-[160px] sm:max-w-[180px] md:max-w-none object-contain"
            />
          </Link>

          {/* DESKTOP NAV */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-10 flex-1 justify-center min-w-0">
            {navLinks.map((link) => {
              const isActive = link.hasDropdown
                ? pathname.startsWith(link.href)
                : link.matchPrefix
                  ? pathname.startsWith(link.matchPrefix)
                  : pathname === link.href ||
                    pathname.startsWith(`${link.href}/`);

              if (link.hasDropdown && link.dropdownKey) {
                const dropdown = navDropdowns[link.dropdownKey];
                const isDropdownOpen = openDropdown === link.dropdownKey;

                return (
                  <div
                    key={link.href}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(link.dropdownKey)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`flex items-center gap-1 text-[14px] geist-semibold transition-colors relative pb-0.5 ${
                        isActive
                          ? "text-[#171717]"
                          : "text-[#525252] hover:text-[#171717]"
                      }`}
                    >
                      {link.label}
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${
                          isDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                      {isActive && (
                        <span className="absolute bottom-[-24px] left-0 right-0 h-[2px] bg-[#171717] rounded-full" />
                      )}
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-80 bg-white border border-neutral-200/70 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.08)] p-2">
                        <div className="space-y-0.5">
                          {dropdown.items.map((item) => {
                            const Icon = item.icon;
                            return (
                              <Link
                                key={item.href}
                                href={item.href}
                                onClick={() => setOpenDropdown(null)}
                                className={`flex items-center gap-3.5 px-3 py-2.5 rounded-xl transition-all group ${
                                  pathname === item.href
                                    ? "bg-neutral-50/80"
                                    : "hover:bg-neutral-50/80"
                                }`}
                              >
                                <div
                                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm border border-neutral-100/50"
                                  style={{ backgroundColor: item.bg }}
                                >
                                  <Icon
                                    className="w-4.5 h-4.5"
                                    style={{ color: item.color }}
                                  />
                                </div>
                                <div>
                                  <p className="text-[14px] geist-semibold text-neutral-800 group-hover:text-[#171717] transition-colors">
                                    {item.label}
                                  </p>
                                  <p className="text-[12px] geist-medium text-neutral-400">
                                    {item.description}
                                  </p>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`text-[14px] geist-semibold transition-colors relative pb-0.5 ${
                    isActive
                      ? "text-[#171717]"
                      : "text-[#525252] hover:text-[#171717]"
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-[-24px] left-0 right-0 h-[2px] bg-[#171717] rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* RIGHT SIDE BUTTONS */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Fixed-width slot so swapping Log in ↔ avatar causes no layout shift */}
            <div className="hidden md:flex justify-end w-[72px] lg:w-[82px]">
              {isAuthenticated ? (
                <AccountMenu user={user} />
              ) : (
                <Link
                  href={LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-8 lg:h-9 px-3.5 lg:px-4.5 text-[12px] lg:text-[13px] geist-semibold text-neutral-600 border border-neutral-200 rounded-full hover:bg-neutral-50 hover:text-neutral-900 transition-all whitespace-nowrap"
                >
                  Log in
                </Link>
              )}
            </div>
            {/* Demo is an acquisition CTA - irrelevant once the user is a customer */}
            {!isAuthenticated && (
              <Link
                href="/contact"
                className="hidden sm:inline-flex items-center justify-center h-8 lg:h-9 px-3.5 lg:px-5 text-[12px] lg:text-[13px] geist-semibold text-white bg-gradient-to-r from-neutral-900 to-neutral-950 hover:from-black hover:to-black rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.20)] transition-all duration-200 whitespace-nowrap"
              >
                <span className="lg:hidden">Demo</span>
                <span className="hidden lg:inline">Book a Demo</span>
              </Link>
            )}

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 -mr-1 text-[#525252] hover:text-[#171717] transition-colors"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              <TextAlignEnd className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE SIDEBAR */}
      {renderSidebar && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Drawer */}
          <div
            className={`fixed left-0 top-0 h-[100dvh] w-[min(320px,88vw)] bg-white/95 backdrop-blur-md z-50 shadow-2xl overflow-y-auto overscroll-contain transform transition-transform duration-300 ease-in-out ${
              animateSidebar ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-neutral-100 px-4 sm:px-5 py-3.5 sm:py-4 flex items-center justify-between">
              <Link href="/" onClick={() => setIsMenuOpen(false)}>
                <img
                  src="/ABSS-LOGO.png"
                  alt="ABSS"
                  className="h-9 w-auto object-contain"
                />
              </Link>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-1 text-[#525252] hover:text-[#171717]"
              >
                <CircleX className="w-5 h-5" />
              </button>
            </div>

            {/* Nav */}
            <nav className="p-3 sm:p-4 space-y-0.5">
              {/* Products accordion */}
              <div>
                <button
                  onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                  className="w-full flex items-center justify-between px-3 py-3 text-[15px] geist-semibold text-[#171717] rounded-[8px] hover:bg-[#F9F9F9]"
                >
                  Products
                  <ChevronDown
                    className={`w-4 h-4 text-[#737373] transition-transform ${mobileProductsOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {mobileProductsOpen && (
                  <div className="ml-2 mt-1 space-y-1">
                    {featuresDropdown.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-[8px] hover:bg-[#F9F9F9]"
                        >
                          <div
                            className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: item.bg }}
                          >
                            <Icon
                              className="w-3.5 h-3.5"
                              style={{ color: item.color }}
                            />
                          </div>
                          <div>
                            <p className="text-[14px] geist-semibold text-[#171717]">
                              {item.label}
                            </p>
                            <p className="text-[12px] geist-medium text-[#737373]">
                              {item.description}
                            </p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Static links */}
              {navLinks
                .filter((item) => !item.hasDropdown)
                .map((link) => {
                  const isLinkActive = link.matchPrefix
                    ? pathname.startsWith(link.matchPrefix)
                    : pathname === link.href ||
                      pathname.startsWith(`${link.href}/`);

                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      className={`block px-3 py-3 text-[15px] geist-semibold rounded-[8px] hover:bg-[#F9F9F9] transition-colors ${
                        isLinkActive ? "text-[#171717]" : "text-[#737373]"
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  );
                })}
            </nav>

            {/* CTA Buttons */}
            <div className="px-4 sm:px-5 pb-6 sm:pb-8 pt-4 space-y-2.5 sm:space-y-3 border-t border-[#F0F0F0]">
              {isAuthenticated ? (
                <AccountMenuMobile
                  user={user}
                  onNavigate={() => setIsMenuOpen(false)}
                />
              ) : (
                <Link
                  href={LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex justify-center items-center w-full h-10 text-[14px] geist-semibold border border-[#E5E5E5] rounded-full text-neutral-700 hover:bg-[#F9F9F9] transition-colors"
                >
                  Log in
                </Link>
              )}
              <Link
                href={isAuthenticated ? ACCOUNT_DASHBOARD_URL : "/contact"}
                target={isAuthenticated ? "_blank" : undefined}
                rel={isAuthenticated ? "noopener noreferrer" : undefined}
                onClick={() => setIsMenuOpen(false)}
                className="flex justify-center items-center w-full h-10 text-[14px] geist-semibold rounded-full text-white bg-gradient-to-r from-neutral-900 to-neutral-950 hover:from-black hover:to-black transition-colors shadow-[0_4px_12px_rgba(0,0,0,0.15)]"
              >
                {isAuthenticated ? "Access Your Apps" : "Book a Demo"}
              </Link>
            </div>

            {/* Trust badge */}
            <div className="px-4 sm:px-5 pb-6 sm:pb-8">
              <div className="flex items-start sm:items-center gap-2 bg-neutral-50/80 border border-neutral-200/60 rounded-2xl px-3 py-2.5">
                <Zap className="w-3.5 h-3.5 text-neutral-800 flex-shrink-0 mt-0.5 sm:mt-0" />
                <p className="text-[10px] sm:text-[11px] geist-medium text-[#737373] leading-snug">
                  India-built · GST compliant · Data stored in India
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
